import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  // Typography/radius live on each size (not the base) so variants whose
  // geometry comes from a CSS class (e.g. `cta`) aren't overridden by the
  // utilities layer. See the `cta` size + `.nav-cta`/`.marketing-cta__button`.
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground hover:bg-[var(--primary-hover)]',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'border border-[var(--accent-1)] bg-transparent text-[var(--accent-1)] hover:border-[var(--secondary-button-hover-border)] hover:bg-[var(--secondary-button-hover-bg)] hover:text-[var(--secondary-button-hover-text)]',
        ghost:
          'hover:bg-[var(--ghost-hover-bg)] hover:text-[var(--color-brand)]',
        link: 'text-primary underline-offset-4 hover:underline',
        // Brand CTA — always-on cyan. Color only; geometry/typography come
        // from the call site's CTA class (see `size: cta`).
        cta: 'bg-[var(--cta-bg)] text-[var(--cta-fg)] hover:bg-[var(--cta-bg-hover)]',
      },
      size: {
        default: 'h-10 rounded-md px-4 py-2 text-sm font-medium',
        sm: 'h-9 rounded-md px-3 text-sm font-medium',
        lg: 'h-11 rounded-md px-8 text-sm font-medium',
        icon: 'h-10 w-10 rounded-md text-sm font-medium',
        // Geometry/typography supplied by the call site's CTA class.
        cta: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
